import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function ScammerAwareness() {
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [submitData, setSubmitData] = useState({
    platform: '',
    username: '',
    description: '',
    evidence: '',
    submitterEmail: '',
    submitterName: '',
    markdownContent: '',
    images: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);

  const [scammers, setScammers] = useState([]);
  const [loadingScammers, setLoadingScammers] = useState(true);

  // Fetch approved scammers from database
  useEffect(() => {
    const fetchScammers = async () => {
      try {
        const response = await fetch('/api/get-public-submissions?type=scammer');
        const result = await response.json();
        
        if (result.success) {
          setScammers(result.submissions);
        } else {
          console.error('Failed to fetch scammers:', result.error);
        }
      } catch (error) {
        console.error('Error fetching scammers:', error);
      } finally {
        setLoadingScammers(false);
      }
    };

    fetchScammers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSubmitData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB limit
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    for (const file of files) {
      if (file.size > maxSize) {
        alert(`File ${file.name} is too large. Maximum size is 5MB.`);
        continue;
      }

      if (!allowedTypes.includes(file.type)) {
        alert(`File ${file.name} is not a supported image type.`);
        continue;
      }

      // Convert to base64 for email attachment
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result;
        const imageData = {
          name: file.name,
          size: file.size,
          type: file.type,
          base64: base64
        };
        
        setUploadedImages(prev => [...prev, imageData]);
        setSubmitData(prev => ({
          ...prev,
          images: [...prev.images, imageData]
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setSubmitData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch('/api/submit-scammer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          platform: submitData.platform,
          username: submitData.username,
          description: submitData.description,
          evidence: submitData.evidence,
          submitterName: submitData.submitterName,
          submitterEmail: submitData.submitterEmail,
          markdownContent: submitData.markdownContent,
          images: uploadedImages
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setSubmitData({
          platform: '',
          username: '',
          description: '',
          evidence: '',
          submitterEmail: '',
          submitterName: '',
          markdownContent: '',
          images: []
        });
        setUploadedImages([]);
        setTimeout(() => {
          setShowSubmitForm(false);
          setSubmitStatus('');
        }, 3000);
      } else {
        setSubmitStatus('error');
        console.error('Submission failed:', result.error);
      }
    } catch (error) {
      console.error('Submission failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="scammers-page">
      <div className="container">
        <div className="page-header">
          <h1>Web3 Scammer Database</h1>
          <p className="page-subtitle">
            A community-driven database of known scammers targeting Web3 developers and projects.
          </p>
          <button 
            className="cta-button" 
            onClick={() => setShowSubmitForm(!showSubmitForm)}
          >
            {showSubmitForm ? 'Cancel' : 'Report Scammer'}
          </button>
        </div>

        {showSubmitForm && (
          <div className="submit-form-container">
            <h2>Report a Scammer</h2>
            <form onSubmit={handleSubmit} className="submit-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="platform">Platform *</label>
                  <select
                    id="platform"
                    name="platform"
                    value={submitData.platform}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Platform</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Upwork">Upwork</option>
                    <option value="Fiverr">Fiverr</option>
                    <option value="Telegram">Telegram</option>
                    <option value="Discord">Discord</option>
                    <option value="Twitter/X">Twitter/X</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="username">Username/Profile Name *</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={submitData.username}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description">Brief Description of Scam *</label>
                <textarea
                  id="description"
                  name="description"
                  value={submitData.description}
                  onChange={handleInputChange}
                  rows="2"
                  placeholder="Brief description of what the scammer did or attempted to do..."
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="markdownContent">Full Report (Markdown)</label>
                <textarea
                  id="markdownContent"
                  name="markdownContent"
                  value={submitData.markdownContent}
                  onChange={handleInputChange}
                  rows="10"
                  placeholder={`# Scammer Report: [Username]

## Overview
Describe the scam in detail...

## How the Scam Works
Explain the scammer's methods...

## Evidence
What evidence do you have?

## Impact
Who was affected?

## Prevention Tips
How can others avoid this scam?

## References
- Screenshot 1
- Conversation logs
- Other reports`}
                />
                <small className="form-help">
                  You can use full Markdown syntax including headers, lists, links, code blocks, etc.
                </small>
              </div>

              {submitData.markdownContent && (
                <div className="form-group">
                  <label>Preview</label>
                  <div className="markdown-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {submitData.markdownContent}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="images">Upload Evidence Images (Optional)</label>
                <input
                  type="file"
                  id="images"
                  name="images"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="file-input"
                />
                <small className="form-help">
                  Maximum 5MB per image. Supported formats: JPEG, PNG, GIF, WebP
                </small>
                
                {uploadedImages.length > 0 && (
                  <div className="uploaded-images">
                    <h4>Uploaded Images:</h4>
                    {uploadedImages.map((image, index) => (
                      <div key={index} className="image-preview">
                        <img src={image.base64} alt={image.name} />
                        <div className="image-info">
                          <span>{image.name}</span>
                          <span>{(image.size / 1024 / 1024).toFixed(2)}MB</span>
                          <button 
                            type="button" 
                            onClick={() => removeImage(index)}
                            className="remove-image"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="submitterName">Your Name *</label>
                  <input
                    type="text"
                    id="submitterName"
                    name="submitterName"
                    value={submitData.submitterName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="submitterEmail">Your Email *</label>
                  <input
                    type="email"
                    id="submitterEmail"
                    name="submitterEmail"
                    value={submitData.submitterEmail}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="form-status success">
                  ✅ Thank you! Your report has been sent for review.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="form-status error">
                  ❌ There was an error. Please try again.
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="cta-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit For Review'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="scammers-table-container">
          {loadingScammers ? (
            <div className="loading-state">
              <p>Loading scammers...</p>
            </div>
          ) : scammers.length === 0 ? (
            <div className="empty-state">
              <p>No approved scammer reports found. Be the first to report one!</p>
            </div>
          ) : (
            <table className="scammers-table">
              <thead>
                <tr>
                  <th>Platform</th>
                  <th>Username</th>
                  <th>Description</th>
                  <th>Reported</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {scammers.map((scammer) => (
                  <tr key={scammer.id}>
                    <td className="platform">{scammer.platform}</td>
                    <td className="username">{scammer.username}</td>
                    <td className="description">{scammer.description}</td>
                    <td className="reported-date">
                      {new Date(scammer.approvedAt).toLocaleDateString()}
                    </td>
                    <td className="details">
                      <a href={`/scammer-details/${scammer.id}`} className="details-link">
                        {scammer.hasFullReport ? '📄 Full Report' : 'View Details'}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default ScammerAwareness;
